import apiClient from '../utils/apiClient';
import { aiLogger, aiCorrelationMiddleware } from '../utils/aiLogger';

class AICopilotService {
  constructor() {
    this.baseUrl = '/copilot';
  }

  /**
   * Simple Q&A endpoint (replaces simpleTest)
   * @param {Object} params - Q&A parameters
   * @param {string} params.question - User's question
   * @param {string} params.systemPrompt - Optional system prompt
   * @param {string} params.correlationId - Optional correlation ID for tracking
   * @returns {Promise<Object>} AI response
   */
  async simpleQA({
    question,
    systemPrompt = "You are a helpful trading assistant.",
    correlationId = null
  }) {
    if (!question || question.trim().length === 0) {
      throw new Error('Question is required');
    }
    
    const { cid } = aiCorrelationMiddleware();
    const effectiveCorrelationId = correlationId || cid;

    try {
      aiLogger.requestStart(cid, 'simpleQA', { 
        questionLength: question.length,
        hasSystemPrompt: !!systemPrompt
      });
      
      const response = await apiClient.post(`${this.baseUrl}/qa`, {
        question: question.trim(),
        system_prompt: systemPrompt,
        correlation_id: effectiveCorrelationId
      });
      
      aiLogger.requestSuccess(cid, 'simpleQA', response.data);
      return response.data;
      
    } catch (error) {
      aiLogger.requestError(cid, 'simpleQA', error, { 
        questionPreview: question.substring(0, 50) 
      });
      throw error;
    }
  }

  /**
   * SIMPLE TEST: Call DeepSeek without authentication (legacy)
   * @param {string} prompt - The prompt to send
   * @returns {Promise<Object>} AI response
   */
  async simpleTest(prompt = "Why is sky blue ?") {
    const { cid } = aiCorrelationMiddleware();

    try {
      aiLogger.requestStart(cid, 'simpleTest', { promptLength: prompt.length });
      
      const response = await apiClient.post(`${this.baseUrl}/simple-test`, {
        prompt: prompt
      });
      
      aiLogger.requestSuccess(cid, 'simpleTest', response.data);
      return response.data;
      
    } catch (error) {
      aiLogger.requestError(cid, 'simpleTest', error, { 
        promptPreview: prompt.substring(0, 50) 
      });
      throw error;
    }
  }

  /**
   * Legacy strategy analysis endpoint
   * @param {Object} strategyParams - Trading strategy parameters
   * @param {Object} marketContext - Optional market context
   * @returns {Promise<Object>} AI analysis results
   */
  async analyzeStrategy(strategyParams, marketContext = {}) {
    const { cid } = aiCorrelationMiddleware();

    try {
      aiLogger.requestStart(cid, 'analyzeStrategy', { 
        strategyParamsKeys: Object.keys(strategyParams),
        hasMarketContext: !!marketContext && Object.keys(marketContext).length > 0
      });
      
      const response = await apiClient.post(`${this.baseUrl}/analyze`, {
        strategy_params: strategyParams,
        market_context: marketContext
      });
      
      aiLogger.requestSuccess(cid, 'analyzeStrategy', response.data);
      return response.data;
      
    } catch (error) {
      aiLogger.requestError(cid, 'analyzeStrategy', error);
      throw error;
    }
  }

  /**
   * Enhanced strategy analysis with strategy type selection
   * @param {Object} params - Strategy analysis parameters
   * @param {string} params.strategyType - Type of strategy: momentum, mean_reversion, breakout, volatility, custom
   * @param {Object} params.strategyParams - Strategy parameters
   * @param {Object} params.marketContext - Market context data
   * @param {string} params.userQuery - Optional natural language question
   * @param {string} params.correlationId - Optional correlation ID for tracking
   * @returns {Promise<Object>} AI analysis response
   */
  async analyzeStrategyEnhanced({
    strategyType = 'momentum',
    strategyParams = {},
    marketContext = {},
    userQuery = null,
    correlationId = null
  }) {
    const { cid } = aiCorrelationMiddleware();
    const effectiveCorrelationId = correlationId || cid;

    try {
      aiLogger.requestStart(cid, 'analyzeStrategyEnhanced', {
        strategyType,
        strategyParamsKeys: Object.keys(strategyParams),
        hasUserQuery: !!userQuery
      });
      
      const response = await apiClient.post(`${this.baseUrl}/analyze-strategy`, {
        strategy_type: strategyType,
        strategy_params: strategyParams,
        market_context: marketContext,
        user_query: userQuery,
        correlation_id: effectiveCorrelationId
      });
      
      aiLogger.requestSuccess(cid, 'analyzeStrategyEnhanced', response.data);
      return response.data;
      
    } catch (error) {
      aiLogger.requestError(cid, 'analyzeStrategyEnhanced', error);
      throw error;
    }
  }

  /**
   * Check AI service health
   * @returns {Promise<Object>} Service health status
   */
  async checkHealth() {
    const { cid } = aiCorrelationMiddleware();
    
    try {
      aiLogger.requestStart(cid, 'checkHealth');
      
      const response = await apiClient.get(`${this.baseUrl}/health`);
      
      aiLogger.requestSuccess(cid, 'checkHealth', response.data);
      return response.data;
      
    } catch (error) {
      aiLogger.requestError(cid, 'checkHealth', error);
      return { 
        enabled: false, 
        status: 'unavailable',
        error: error.message 
      };
    }
  }

  /**
   * Get current user's AI usage statistics
   * @returns {Promise<Object>} Usage stats
   */
  async getUsageStats() {
    const { cid } = aiCorrelationMiddleware();
    
    try {
      aiLogger.requestStart(cid, 'getUsageStats');
      
      const response = await apiClient.get(`${this.baseUrl}/usage`);
      
      aiLogger.requestSuccess(cid, 'getUsageStats', response.data);
      return response.data;
      
    } catch (error) {
      aiLogger.requestError(cid, 'getUsageStats', error);
      return null;
    }
  }

  /**
   * Get detailed usage statistics for all endpoint types
   * @returns {Promise<Object>} Detailed usage information
   */
  async getDetailedUsageStats() {
    const { cid } = aiCorrelationMiddleware();
    
    try {
      aiLogger.requestStart(cid, 'getDetailedUsageStats');
      
      const response = await apiClient.get(`${this.baseUrl}/usage/detailed`);
      
      aiLogger.requestSuccess(cid, 'getDetailedUsageStats', response.data);
      return response.data;
      
    } catch (error) {
      aiLogger.requestError(cid, 'getDetailedUsageStats', error);
      return null;
    }
  }

  /**
   * Format AI analysis for display
   * @param {Object} analysis - Raw AI analysis
   * @returns {Object} Formatted analysis
   */
  formatAnalysis(analysis) {
    if (!analysis.success || !analysis.analysis) {
      let userMessage = analysis.error || 'AI analysis failed';
      
      // DETECT CREDIT ERRORS
      const errorStr = String(analysis.error || '').toLowerCase();
      if (errorStr.includes('credits exhausted') || 
          errorStr.includes('insufficient balance') ||
          errorStr.includes('platform.deepseek.com') ||
          errorStr.includes('402') || // HTTP 402 Payment Required
          errorStr.includes('payment required')) {
        
        userMessage = '⚠️ DeepSeek API Credits Exhausted\n' +
                     'Add credits at: https://platform.deepseek.com\n' +
                     '(Your app rate limit: 2/50 used)';
      }
      
      return {
        success: false,
        error: userMessage,
        summary: 'Unable to analyze strategy'
      };
    }

    const aiData = analysis.analysis;
    
    return {
      success: true,
      cached: analysis.cached || false,
      summary: aiData.summary || 'No summary available',
      strengths: aiData.strengths || [],
      weaknesses: aiData.weaknesses || [],
      optimizations: aiData.optimizations || [],
      riskAssessment: aiData.risk_assessment || { level: 5, factors: [], mitigations: [] },
      marketConditions: aiData.market_conditions || { best_for: [], worst_for: [] },
      confidenceScore: aiData.confidence_score || 0.5,
      rawData: aiData,
      timestamp: analysis.timestamp
    };
  }

  /**
   * Generate risk level color and text
   * @param {number} riskLevel - Risk level 1-10
   * @returns {Object} Color and label
   */
  getRiskLevelInfo(riskLevel) {
    if (riskLevel === null || riskLevel === undefined) {
      return { color: '#6b7280', label: 'Credit Error', icon: '⚫' };
    }
    if (riskLevel <= 3) {
      return { color: '#10b981', label: 'Low Risk', icon: '🟢' };
    } else if (riskLevel <= 6) {
      return { color: '#f59e0b', label: 'Medium Risk', icon: '🟡' };
    } else {
      return { color: '#ef4444', label: 'High Risk', icon: '🔴' };
    }
  }

  /**
   * Format enhanced strategy analysis response
   * @param {Object} response - Enhanced AI response
   * @returns {Object} Formatted analysis
   */
  formatEnhancedAnalysis(response) {
    if (!response.analysis) {
      return {
        success: false,
        error: 'Invalid AI response',
        summary: 'Unable to analyze strategy'
      };
    }

    return {
      success: true,
      analysis: response.analysis,
      insights: response.insights || [],
      recommendations: response.recommendations || [],
      warnings: response.warnings || [],
      strategySpecificTips: response.strategy_specific_tips || [],
      timestamp: response.timestamp,
      correlationId: response.correlation_id,
      queryType: response.query_type,
      strategyType: response.strategy_type
    };
  }

  /**
   * Format free-form Q&A response
   * @param {Object} response - Q&A AI response
   * @returns {Object} Formatted response
   */
  formatQAResponse(response) {
    if (!response.analysis) {
      return {
        success: false,
        error: 'Invalid AI response',
        summary: 'Unable to process question'
      };
    }

    return {
      success: true,
      answer: response.analysis,
      insights: response.insights || [],
      timestamp: response.timestamp,
      correlationId: response.correlation_id,
      queryType: response.query_type,
    };
  }
  
}

// Singleton instance
export const aiCopilotService = new AICopilotService();